import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { 
  Users, 
  UserPlus, 
  Building2, 
  Settings, 
  TrendingUp, 
  Calendar,
  Mail,
  Clock,
  ChevronRight,
  Plus,
  Shield
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useAuth as useAuthHook } from '../hooks/useAuth';
import { invitesApi } from '../lib/api/invites';
import { clubsApi } from '../lib/api/clubs';
import TopBar from '../components/TopBar';
import { createPlatformAdminHomeMobileMenu } from '../components/MobileContextMenu';
import Button from '../components/Button';

interface StatsCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  trend?: string;
  color?: 'default' | 'green' | 'blue' | 'purple';
}

function StatsCard({ title, value, icon, trend, color = 'default' }: StatsCardProps) {
  const colorClasses = {
    default: 'bg-oro-surface-alt text-text-muted',
    green: 'bg-green-500/10 text-green-400',
    blue: 'bg-blue-500/10 text-blue-400',
    purple: 'bg-purple-500/10 text-purple-400'
  };

  return (
    <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
      <div className="flex items-start justify-between mb-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        {trend && (
          <span className="text-xs text-green-400 font-medium">
            +{trend}
          </span>
        )}
      </div>
      <div className="text-2xl font-bold text-text-primary mb-1">
        {value}
      </div>
      <div className="text-sm text-text-muted">
        {title}
      </div>
    </div>
  );
}

interface QuickActionProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  onClick: () => void;
  variant?: 'default' | 'primary';
}

function QuickAction({ title, description, icon, onClick, variant = 'default' }: QuickActionProps) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-4 rounded-xl border transition-all ${
        variant === 'primary'
          ? 'bg-accent/5 border-accent/20 hover:bg-accent/10'
          : 'bg-oro-surface border-oro-border hover:border-oro-border-hover'
      }`}
    >
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-lg ${
          variant === 'primary' 
            ? 'bg-accent/20 text-accent' 
            : 'bg-oro-surface-alt text-text-muted'
        }`}>
          {icon}
        </div>
        <div className="flex-1">
          <h3 className="font-medium text-text-primary mb-1">{title}</h3>
          <p className="text-sm text-text-muted">{description}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-text-muted" />
      </div>
    </button>
  );
}

export default function PlatformAdminHomePage() {
  const navigate = useNavigate();
  const { me } = useAuth();
  const { logout } = useAuthHook();
  
  // Create mobile menu configuration
  const adminMenu = createPlatformAdminHomeMobileMenu(navigate, logout);

  // Redirect non-admins
  useEffect(() => {
    if (me && !me.is_platform_admin) {
      navigate('/');
    }
  }, [me, navigate]);

  // Fetch platform statistics
  const { data: invitesData } = useQuery({
    queryKey: ['admin', 'invites'],
    queryFn: () => invitesApi.listInvites({}),
  });

  const { data: clubsData } = useQuery({
    queryKey: ['admin', 'clubs'],
    queryFn: () => clubsApi.listAllClubs(),
  });

  const { data: waitlistData } = useQuery({
    queryKey: ['admin', 'waitlist'],
    queryFn: () => invitesApi.viewWaitlist({}),
  });

  // Calculate stats
  const totalClubs = clubsData?.length || 0;
  const totalInvites = invitesData?.invites?.length || 0;
  const pendingInvites = invitesData?.invites?.filter(i => i.status === 'pending').length || 0;
  const totalWaitlist = waitlistData?.waitlist?.length || 0;

  const quickActions = [
    {
      title: 'Create Club',
      description: 'Set up a new running club on the platform',
      icon: <Plus className="w-5 h-5" />,
      onClick: () => navigate('/clubs/create'),
      variant: 'primary' as const
    },
    {
      title: 'Manage Invites',
      description: 'Create and manage platform invitations',
      icon: <UserPlus className="w-5 h-5" />,
      onClick: () => navigate('/admin/invites')
    },
    {
      title: 'View All Clubs',
      description: 'Browse and manage all platform clubs',
      icon: <Building2 className="w-5 h-5" />,
      onClick: () => navigate('/platform-admin/clubs')
    },
    {
      title: 'Platform Settings',
      description: 'Configure global platform settings',
      icon: <Settings className="w-5 h-5" />,
      onClick: () => navigate('/platform-admin/settings')
    }
  ];

  if (!me?.is_platform_admin) {
    return (
      <div className="min-h-screen bg-oro-bg flex items-center justify-center p-4">
        <div className="bg-oro-surface border border-oro-border rounded-xl p-6 text-center">
          <Shield className="w-12 h-12 text-text-muted mx-auto mb-4" />
          <h2 className="font-semibold text-text-primary mb-2">Access Restricted</h2>
          <p className="text-text-secondary">Platform admin privileges required.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-oro-bg">
      <TopBar 
        title="Platform Admin" 
        onBack={() => navigate('/')}
        mobileMenu={adminMenu}
      />

      <div className="safe-area-top pt-4 px-4 pb-20">
        {/* Welcome Section */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            Welcome back, {me?.first_name}
          </h1>
          <p className="text-text-muted">
            Manage your running club platform from here
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 gap-4 mb-6">
          <StatsCard
            title="Total Clubs"
            value={totalClubs}
            icon={<Building2 className="w-5 h-5" />}
            color="blue"
          />
          <StatsCard
            title="Active Invites"
            value={pendingInvites}
            icon={<Mail className="w-5 h-5" />}
            color="green"
          />
          <StatsCard
            title="Total Invites"
            value={totalInvites}
            icon={<UserPlus className="w-5 h-5" />}
            color="purple"
          />
          <StatsCard
            title="Waitlist"
            value={totalWaitlist}
            icon={<Clock className="w-5 h-5" />}
          />
        </div>

        {/* Quick Actions */}
        <div className="mb-6">
          <h2 className="font-semibold text-text-primary mb-4">Quick Actions</h2>
          <div className="space-y-3">
            {quickActions.map((action, index) => (
              <QuickAction
                key={index}
                title={action.title}
                description={action.description}
                icon={action.icon}
                onClick={action.onClick}
                variant={action.variant}
              />
            ))}
          </div>
        </div>

        {/* Recent Activity Section */}
        <div className="bg-oro-surface border border-oro-border rounded-xl p-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium text-text-primary">Platform Overview</h3>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => navigate('/clubs')}
            >
              View All
            </Button>
          </div>
          
          <div className="space-y-3 text-sm">
            <div className="flex items-center gap-3 p-3 bg-oro-surface-alt rounded-lg">
              <TrendingUp className="w-4 h-4 text-green-400" />
              <div className="flex-1">
                <div className="font-medium text-text-primary">Platform Growth</div>
                <div className="text-text-muted">{totalClubs} clubs created</div>
              </div>
            </div>
            
            <div className="flex items-center gap-3 p-3 bg-oro-surface-alt rounded-lg">
              <Calendar className="w-4 h-4 text-blue-400" />
              <div className="flex-1">
                <div className="font-medium text-text-primary">Invite System</div>
                <div className="text-text-muted">{pendingInvites} pending invitations</div>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-oro-surface-alt rounded-lg">
              <Users className="w-4 h-4 text-purple-400" />
              <div className="flex-1">
                <div className="font-medium text-text-primary">User Interest</div>
                <div className="text-text-muted">{totalWaitlist} people on waitlist</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}